import areEqual from "fbjs/lib/areEqual";
import * as React from "react";
import ReactDOM from "react-dom";
import { Address, Kladr as BaseKladr, KladrValue } from "react-ui";

import { PicklistDataItem } from "../../../Engine/Core/Picklists/PicklistTypes";
import { ControlDependency } from "../../withRuntimeDependencies";
import { pullContext } from "../../TypingUtils";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { IScrollToContext, scrollToContextTypes } from "../../Internals/Contexts/ScrollToContext";
import { toHtmlElementOrNull } from "../../Commons/DOM/DomHelpers";

import { Root } from "./Kladr.styles";

export type KladrField =
    | "index"
    | "house"
    | "building"
    | "room"
    | "district"
    | "city"
    | "settlement"
    | "street"
    | "regionCode"
    | "address"; // В ФНС

export type ModelAddress = { [key in KladrField]: string | undefined | null };

export interface KladrProps {
    dependencies?: ControlDependency[];
    modelSet: (fieldName: KladrField, fieldValue: undefined | null | string) => void;
    title: string;
    error?: string;
    warning?: string;
    isTooltipEnabled?: boolean;
    address: ModelAddress;
    isReadOnly?: boolean;
    "data-tid"?: string;
    onGetRegions: () => Promise<PicklistDataItem[]>;
}

interface State {
    value: KladrValue | "";
}

type KladrContext = IScrollToContext;

export class Kladr extends React.Component<KladrProps, State> {
    public static contextTypes = {
        ...scrollToContextTypes,
    };

    public state: State = { value: "" };
    private mount?: boolean;

    public componentDidMount(): void {
        if (!this.state.value) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.initAddress();
        }

        this.mount = true;
    }

    public UNSAFE_componentWillReceiveProps(nextProps: KladrProps): void {
        if (!areEqual(this.props.address, nextProps.address)) {
            this.resetStateIfModelIsEmpty(nextProps.address);
        }
    }

    public shouldComponentUpdate(nextProps: Readonly<KladrProps>, nextState: Readonly<State>): boolean {
        const { address: oldAddress } = this.props;
        const { address: newAddress } = nextProps;

        return (
            this.props.error !== nextProps.error ||
            this.props.warning !== nextProps.warning ||
            this.props.isTooltipEnabled !== nextProps.isTooltipEnabled ||
            !areEqual(oldAddress, newAddress) ||
            !areEqual(this.state.value, nextState.value)
        );
    }

    public componentWillUnmount(): void {
        this.mount = false;
    }

    public render(): JSX.Element {
        const { error, warning, title } = this.props;

        return (
            <FocusManagementObserver onRequestFocus={this.handleRequestFocus}>
                <Root>
                    <BaseKladr
                        error={error}
                        title={title}
                        data-tid={this.props["data-tid"]}
                        value={this.state.value}
                        warning={warning}
                        onChange={this.onChange}
                        isReadOnly={this.props.isReadOnly}
                    />
                </Root>
            </FocusManagementObserver>
        );
    }

    private readonly handleRequestFocus = (): void => {
        const kladr = toHtmlElementOrNull(ReactDOM.findDOMNode(this));

        if (kladr != undefined) {
            this.getContext().scrollTo(kladr);
        }
    };

    private readonly onChange = (_: React.SyntheticEvent, value: KladrValue) => {
        this.setState({ value: value });

        for (const elem of Object.keys(value.address)) {
            switch (elem) {
                case "index":
                case "house":
                case "building":
                case "room":
                    this.props.modelSet(elem, value.address[elem]);
                    break;
                case "region": {
                    const regionCode =
                        value.address.region && value.address.region.code ? value.address.region.code.substr(0, 2) : "";
                    this.props.modelSet("regionCode", regionCode);
                    break;
                }
                case "district":
                case "city":
                case "settlement":
                case "street": {
                    const addressItem = value.address[elem];
                    const elemValue = addressItem ? `${addressItem.name} ${addressItem.abbreviation || ""}` : "";
                    this.props.modelSet(elem, elemValue);
                    break;
                }
                default:
                    break;
            }
        }
    };

    private setAddress(regions: Array<{ code: string; name: string }>): void {
        const address: Partial<Address> = {};

        Object.keys(this.props.address).forEach(propName => {
            const value = this.props.address[propName as keyof ModelAddress];
            if (!value) {
                return;
            }

            switch (propName) {
                case "index":
                case "house":
                case "building":
                case "room":
                    address[propName] = value;
                    break;
                case "regionCode":
                    // Тут всё глючит, легко правится, но мне сейчас лень запускать и всё проверять
                    // @ts-ignore
                    address.region = address.region ?? {
                        // eslint-disable-next-line no-constant-binary-expression
                        code: `${value}00000000000` || "",
                        name: "",
                    };
                    regions.forEach(region => {
                        if (region.code === value) {
                            // @ts-ignore
                            address.region.name = region.name;
                        }
                    });
                    break;
                case "district":
                case "city":
                case "settlement":
                case "street":
                    // Тут всё глючит, легко правится, но мне сейчас лень запускать и всё проверять
                    // @ts-ignore
                    address[propName] = address[propName] || {};
                    // @ts-ignore
                    address[propName].name = value;
                    // @ts-ignore
                    address[propName].abbreviation = "";
                    break;
                default:
                    break;
            }
        });

        if (this.mount) {
            // @ts-ignore
            this.setState({ value: { address: address } });
        }
    }

    private resetStateIfModelIsEmpty(address: ModelAddress): void {
        const isEmptyAddress = Object.values(address).every(value => value == undefined || value === "");

        if (isEmptyAddress) {
            // @ts-ignore
            this.setState({ value: { address: {} } });
        }
    }

    private async initAddress(): Promise<void> {
        const regions = await this.props.onGetRegions();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unsafe-argument
        this.setAddress(regions as any);
    }

    private getContext(): KladrContext {
        return pullContext<KladrContext>(this);
    }
}
